import React, { useContext, useEffect, useState } from "react"
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { ChevronLeft, Plus, Trash2 } from "lucide-react"
import { Label } from "components/ui/label"
import { Card, CardContent } from "components/ui/card"
import { Input } from "components/ui/input"
import { Button } from "components/ui/button"
import { useLocation, useNavigate } from "react-router-dom"
import CustomSelectGroup, {
  CustomSelectItemsType,
} from "components/common/selectGroup/CustomSelectGroup"
import {
  INVOICE_DEFAULT_VALUES,
  INVOICE_REDUCTION_BASE, INVOICE_REDUCTION_TYPE,
  InvoiceFormFields,
  REDUCTIONS_LABEL,
} from "@/constants/InvoiceConstants"
import { useAppDispatch, useAppSelector } from "@/hooks/reduxToolkitHook"
import CustomDatePicker from "components/common/datepicker/CustomDatePicker"
import { InvoiceFormType } from "@/types/InvoicesType"
import moment from "moment/min/moment-with-locales"
import {
  createAsyncInvoice,
  updateAsyncInvoice,
} from "@/redux/features/invoicesSlice"
import {
  ActionNotificationContext,
  ActionNotificationContextValeurType,
} from "@/context/ActionNotificationContext"
import Combobox from "components/ui/combobox"
import colors from "tailwindcss/colors"
import { cn } from "@/utils/shadcn-utils"
import { getCompany } from "@/requests/companiesRequests"
import { INVOICES_URL } from "@/constants/mainRoutes"
import { StatusEnum } from "@/constants/CodePromoConstants"
import { SubscriptionType } from "@/types/SubscriptionsType"
import InvoiceSettingsPopOver from "@/views/facturation/components/InvoiceSettingsPopOver"

export const getReductionLabel = (base: string, type: string, value: number) => {
  if( base === INVOICE_REDUCTION_BASE.TOTAL) {
    if(type === INVOICE_REDUCTION_TYPE.PERCENTAGE){
      return REDUCTIONS_LABEL.TOTAL_AND_PERCENT.replace(":p", value.toString())
    }else {
      return REDUCTIONS_LABEL.TOTAL_AND_FIXED
    }
  }else{
    if(type === INVOICE_REDUCTION_TYPE.PERCENTAGE){
      return REDUCTIONS_LABEL.PER_COLLABO_AND_PERCENT.replace(":p", value.toString())
    }else {
      return REDUCTIONS_LABEL.PER_COLLABO_AND_FIXED
    }
  }
}

// Composant principal
const AddOrModifyInvoice: React.FC = () => {
  moment.locale("fr-FR")
  const navigate = useNavigate()
  // updating invoice id
  const { state } = useLocation()
  const invoiceId = state?.invoiceId
  const isUpdate = !!invoiceId

  const dispatch = useAppDispatch()
  const { displayAlert } = useContext(
    ActionNotificationContext
  ) as ActionNotificationContextValeurType

  const { companies } = useAppSelector((state) => state.companies)
  const { invoices } = useAppSelector(state => state.invoices)
  const [companiesName, setCompaniesName] = useState<CustomSelectItemsType>([])
  const [collaboPrice, setCollaboPrice] = useState<number>(0)
  const [subscription, setSubscription] = useState<SubscriptionType>()
  const [hasReduction, setHasReduction] = useState<boolean>(false)

  const {
    register,
    setValue,
    reset,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<InvoiceFormType>({
    defaultValues: INVOICE_DEFAULT_VALUES,
  })

  const { fields, append, prepend, remove } = useFieldArray({
    control,
    name: InvoiceFormFields.line_items,
  })

  // Surveiller tous les items et la réduction et la tva
  const watchItems = watch(InvoiceFormFields.line_items)
  const watchCollaboQuantity = watch(`${InvoiceFormFields.line_items}.${0}.quantity`)
  const watchReductionValue = watch(InvoiceFormFields.reductionValue)
  const watchReductionType = watch(InvoiceFormFields.reductionType)
  const watchReductionBase = watch(InvoiceFormFields.reductionBase)
  const watchTva = watch(InvoiceFormFields.tva)

  const watchCompanyId = watch(InvoiceFormFields.company)

  // Calculer le montant pour chaque ligne
  const calculateItemAmount = (
    quantity: string | number,
    unitPrice: string | number,
    reduction: string | number,
  ): string => {
    const qty = typeof quantity === "string" ? parseFloat(quantity) : quantity
    const price = typeof unitPrice === "string" ? parseFloat(unitPrice) : unitPrice
    const reduction_value  = typeof reduction === "string" ? parseFloat(reduction) : reduction
    const line_total = qty * price
    const reduction_amount = watchReductionType === INVOICE_REDUCTION_TYPE.FIXED ?
      reduction_value :
      line_total * reduction_value / 100
    return ( line_total - reduction_amount).toFixed(2)
  }

  const itemReductionAmountSum = (): number => {
    return watchItems.reduce((total: number, item) => {
      return total + (parseFloat(item.reduction_value) || 0)
    }, 0)
  }

  const itemReductionAmountPercentageCalculate = (): number => {
    return watchItems.reduce((total: number, item) => {
      const amount = (item.quantity * item.unit_price * item.reduction_value / 100 )  || 0
      return total + amount
    }, 0)
  }

  // Calculer le total HT
  const calculateTotalHT = (): number => {
    return watchItems.reduce((total: number, item) => {
      const amount = parseFloat(calculateItemAmount(item.quantity, item.unit_price, "0")) || 0
      return total + amount
    }, 0)
  }

  // Calculer le montant de la reduction
  const calculateReductionAmount = (): number  =>{
    if(!hasReduction){
      return 0
    } else {
      if(watchReductionBase === INVOICE_REDUCTION_BASE.TOTAL){
        if(watchReductionType === INVOICE_REDUCTION_TYPE.FIXED){
          return parseFloat(watchReductionValue) || 0
        } else{
          const reduction_base = watchReductionBase === INVOICE_REDUCTION_BASE.TOTAL ? calculateTotalHT() : collaboPrice
          const minus = parseFloat(watchReductionValue) || 0
          return (reduction_base * minus) / 100
        }
      }else{
        if(watchReductionType === INVOICE_REDUCTION_TYPE.FIXED){
          return itemReductionAmountSum()
        } else{
          return itemReductionAmountPercentageCalculate()
        }
      }
    }
  }

  // Calculer le montant après réduction
  const calculateAfterReduction = (): number => {
    const totalHT = calculateTotalHT()
    const reduction = calculateReductionAmount()
    return totalHT - reduction
  }

  // Calculer la taxe
  const calculateTax = (): number => {
    return (calculateAfterReduction() * watchTva) / 100
  }

  // Calculer le total TTC
  const calculateTotalTTC = (): number => {
    return calculateAfterReduction() + calculateTax()
  }

  const onSubmit = (data: InvoiceFormType) => {
    const subtotal = calculateAfterReduction()
    const totalBrut = calculateTotalHT()
    // const reductionPercentage = 100 * ( 1 - subtotal/totalBrut)
    // console.log(reductionPercentage)
    const invoiceData = {
      ...data,
      // [InvoiceFormFields.tva]: watchTva
      [InvoiceFormFields.reductionValue]: watchReductionValue || '0',
      [InvoiceFormFields.grossSubtotal]: totalBrut.toFixed(2),
      [InvoiceFormFields.totalReduction]: (totalBrut - subtotal).toFixed(2),
      [InvoiceFormFields.subtotal]: subtotal.toFixed(2),
      [InvoiceFormFields.taxAmount]: calculateTax().toFixed(2),
      [InvoiceFormFields.total]: calculateTotalTTC().toFixed(2),
    }
    let recalculatedLineItems = invoiceData.line_items ?? []
    for (let i = 0; i < recalculatedLineItems.length; i++) {
      recalculatedLineItems[i].reduction_value = recalculatedLineItems[i].reduction_value ?? 0
      recalculatedLineItems[i].amount = calculateItemAmount(
        recalculatedLineItems[i].unit_price,
        recalculatedLineItems[i].quantity,
        recalculatedLineItems[i].reduction_value ?? 0
      )
    }
    invoiceData.line_items = recalculatedLineItems

    try{
      if (isUpdate) {
        dispatch(updateAsyncInvoice({ id: invoiceId, data: invoiceData })).then(
          (result) => {
            if (updateAsyncInvoice.fulfilled.match(result)) {
              displayAlert("Operation effectuée avec succès", "success")
              reset()
              navigate(INVOICES_URL)
            } else {
              if (updateAsyncInvoice.rejected.match(result)) {
                displayAlert(
                  "Une erreur est survenue lors de la modification de la facture",
                  "error"
                )
              }
            }
          }
        )
      } else {
        dispatch(createAsyncInvoice(invoiceData)).then((result) => {
          if (createAsyncInvoice.fulfilled.match(result)) {
            displayAlert("Operation effectuée avec succès", "success")
            reset()
            navigate(INVOICES_URL)
          } else {
            if (createAsyncInvoice.rejected.match(result)) {
              displayAlert(
                "Une erreur est survenue lors de l'émission de la facture",
                "error"
              )
            }
          }
        })
      }
    } catch (e) {
      displayAlert(
        "Une erreur est survenue lors de l'émission de la facture",
        "error"
      )
    }
  }

  // set companies name filtered by  inactive or null subscription
  useEffect(() => {
    // && moment(company?.subscription?.start_date).isBefore(moment())
    setCompaniesName(
      companies?.filter( company => company.subscription && company.subscription?.status == StatusEnum.ACTIVE )
        .map((company) => ({ label: company.name, value: company.id }))
    )
  }, [companies])

  // pre remplissage dans le cas d'une modification de facture
  useEffect(() => {
    const invoice = invoices.find(invoice => invoice.id === invoiceId)
    setValue(
      InvoiceFormFields.echeance as keyof InvoiceFormType,
      moment().add(30, "days").format("YYYY-MM-DD")
    )
    if(invoice){
      // console.log(invoice.line_items)
      setValue(InvoiceFormFields.company as keyof InvoiceFormType, invoice.company)
      setValue(InvoiceFormFields.echeance as keyof InvoiceFormType, invoice.due_date)
      setValue(InvoiceFormFields.reductionType as keyof InvoiceFormType, invoice.global_reduction_type)
      setValue(InvoiceFormFields.reductionValue as keyof InvoiceFormType, invoice.global_reduction_value)
      setValue(InvoiceFormFields.reductionBase as keyof InvoiceFormType, invoice.global_reduction_base)
      setValue(InvoiceFormFields.tva as keyof InvoiceFormType, invoice.tva_rate)
      setValue(InvoiceFormFields.line_items as keyof InvoiceFormType, invoice.line_items)
      setHasReduction(invoice.global_reduction_value > 0 || invoice.total_reduction_amount > 0)
    }
  }, [invoices, companiesName])

  // pre-filling on company choose
  useEffect(() => {
    if(!isUpdate){
      setValue(
        InvoiceFormFields.echeance as keyof InvoiceFormType,
        moment().add(30, "days").format("YYYY-MM-DD")
      )
      if (watchCompanyId) {
        getCompany(watchCompanyId).then((response) => {
          const data = response.data
          // console.log(data)
          setSubscription(data.subscription)

          // calculate de collabo price or base_price by collabo number
          const price =
            data?.subscription?.collabos_number > 5
              ? data?.subscription?.collabos_number *
                data?.subscription?.plan?.price_by_collabo
              : data?.subscription?.plan?.base_price
          setCollaboPrice(price)

          // set reduction values here
          if (data?.subscription?.reduction) {
            setValue(
              InvoiceFormFields.reductionValue as keyof InvoiceFormType,
              parseFloat(data?.subscription?.reduction?.discount_value)
            )
            setValue(
              InvoiceFormFields.reductionBase as keyof InvoiceFormType,
              data?.subscription?.reduction?.apply_to === "PER_COLLABO"
                ? INVOICE_REDUCTION_BASE.PER_LINE
                : INVOICE_REDUCTION_BASE.TOTAL
            )
            setValue(
              InvoiceFormFields.reductionType as keyof InvoiceFormType,
              data?.subscription?.reduction?.discount_type
            )
            setHasReduction(true)
          } else {
            setValue(
              InvoiceFormFields.reductionValue as keyof InvoiceFormType,
              "0"
            )
            setValue(
              InvoiceFormFields.reductionBase as keyof InvoiceFormType,
              INVOICE_REDUCTION_BASE.TOTAL
            )
            setValue(
              InvoiceFormFields.reductionType as keyof InvoiceFormType,
              INVOICE_REDUCTION_TYPE.PERCENTAGE
            )
            setHasReduction(false)
          }
          let line_items = [
            {
              [InvoiceFormFields.description]: "",
              [InvoiceFormFields.quantity]:
                data?.subscription?.collabos_number ?? "0",
              [InvoiceFormFields.unit_price]:
                data?.subscription?.collabos_number > 5
                  ? data?.subscription?.plan?.price_by_collabo
                  : "0",
              [InvoiceFormFields.amount]: price,
              [InvoiceFormFields.item_reduction]: parseFloat(
                data?.subscription?.reduction?.discount_value ?? "0"
              ),
            },
          ]
          if (data?.subscription?.addons?.length) {
            data?.subscription?.addons.map((addon) => {
              line_items = [
                ...line_items,
                {
                  [InvoiceFormFields.description]: `Add-on - ${addon?.label}` ?? "",
                  [InvoiceFormFields.quantity]: 1,
                  [InvoiceFormFields.unit_price]: addon?.amount ?? 0,
                  [InvoiceFormFields.amount]: addon?.amount ?? 0,
                  [InvoiceFormFields.item_reduction]: 0,
                },
              ]
            })
          }
          setValue(InvoiceFormFields.line_items, line_items)
        })
      }
    }
  }, [watchCompanyId])

  // automatically set qty to 1 and price to base_price when collabo_number < 6
  useEffect(() => {
    if(!isUpdate){
      if (watchCollaboQuantity < 6 && watchCollaboQuantity > 1) {
        setValue(`${InvoiceFormFields.line_items}.${0}.quantity`, 1)
      }
      const price = subscription
        ? watchCollaboQuantity > 5
          ? subscription.plan.price_by_collabo
          : subscription.plan.base_price
        : 0

      setValue(`${InvoiceFormFields.line_items}.${0}.unit_price`, price)
    }
  }, [watchCollaboQuantity])

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 ">
      <div className="mb-5">
        <div>
          <Button
            variant="link"
            className="text-blue-550 p-0"
            onClick={() => navigate(-1)}>
            <ChevronLeft size="20" /> Retour
          </Button>
        </div>
        <div className="flex items-baseline gap-2">
          <h2 className="subtitle">
            {isUpdate ? "Modifier" : "Emettre"} une facture
          </h2>
        </div>
      </div>

      <div className=" mx-auto bg-white py-8 px-6 sm:px-8 lg:px-12">
        <div>
          <>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Infos génerales */}

              <div className="space-y-4">
                <div className="flex justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Informations générales
                  </h3>

                  <InvoiceSettingsPopOver
                    errors={errors}
                    control={control}
                    watchReductionType={watchReductionType}
                    watchReductionBase={watchReductionBase}
                    register={register}
                    hasReduction={hasReduction}
                    setHasReduction={setHasReduction}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Client */}
                  <div className="flex flex-col justify-between">
                    <Label htmlFor={InvoiceFormFields.company}>Client</Label>
                    <Controller
                      name={InvoiceFormFields.company}
                      control={control}
                      rules={{ required: "Veuillez sélectionner un client" }}
                      render={({ field }) => (
                        <Combobox
                          options={companiesName}
                          onChange={field.onChange}
                          value={field.value}
                          placeholder="Selectionner le client"
                          disabled={isUpdate}
                          className={cn(isUpdate && "cursor-not-allowed")}
                        />
                      )}
                    />
                    <ErrorMessage
                      errors={errors}
                      name={InvoiceFormFields.company}
                      render={({ message }) => (
                        <p className="text-sm text-red-500">{message}</p>
                      )}
                    />
                  </div>
                  {/* Date d'échéance */}
                  <div>
                    <Controller
                      name={InvoiceFormFields.echeance}
                      control={control}
                      rules={{
                        required: "La date d'échéance est requise",
                        // validate: (value: string) => value.length !== 10 ?
                        //   "Format de date invalide" : ""
                      }}
                      render={({ field }) => (
                        <CustomDatePicker
                          name={InvoiceFormFields.echeance}
                          label="Date d'échéance"
                          value={field.value}
                          placeholder="AAAA-MM-JJ"
                          onChange={(e: string) => field.onChange(e)}
                          // disabled={isUpdate}
                        />
                      )}
                    />
                    <ErrorMessage
                      errors={errors}
                      name={InvoiceFormFields.echeance}
                      render={({ message }) => (
                        <p className="text-sm text-red-500">{message}</p>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Éléments de la facture */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-black4">
                    Éléments de la facture
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    // className={cn(isUpdate && "cursor-not-allowed")}
                    onClick={() => {
                      // if (!isUpdate) {
                        append({
                          [InvoiceFormFields.description]: "",
                          [InvoiceFormFields.quantity]: "1",
                          [InvoiceFormFields.unit_price]: "0",
                          [InvoiceFormFields.amount]: "0",
                          [InvoiceFormFields.item_reduction]: "0",
                        })
                      // }
                    }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un élément
                  </Button>
                </div>

                <div className='space-y-4'>
                  {/*className={cn("space-y-4", isUpdate && "cursor-not-allowed")}>*/}
                  {fields.map((field, index) => (
                    <Card key={field.id} className="relative shadow-none">
                      {/* Bouton supprimer */}
                      <div className="space-y-2 absolute right-6 top-[-5px]">
                        <Label className="invisible">Action</Label>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1 /*|| isUpdate}*/}
                          className={cn("w-[25px] border-0")}>
                          <Trash2 size={20} color={colors.red["500"]} />
                        </Button>
                      </div>
                      <CardContent className="pt-6">
                        <div className="space-y-4">
                          {/* Description de l'élément */}
                          <div className="space-y-2">
                            <Label
                              htmlFor={`${InvoiceFormFields.line_items}.${index}.description`}>
                              Description{" "}
                            </Label>
                            <Input
                              {...register(
                                `${InvoiceFormFields.line_items}.${index}.description`,
                                {
                                  required: "La description est requise",
                                }
                              )}
                              // disabled={isUpdate}
                              placeholder="Ex: Abonnement Uptimise (January 20XX) - Offre STANDARD"
                            />
                            <ErrorMessage
                              errors={errors}
                              name={`${InvoiceFormFields.line_items}.${index}.description`}
                              render={({ message }) => (
                                <p className="text-sm text-red-500">
                                  {message}
                                </p>
                              )}
                            />
                          </div>

                          <div className=" flex flex-col md:flex-row flex-no-wrap gap-4">
                            {/* Quantité */}
                            <div className="space-y-2 w-full">
                              <Label
                                htmlFor={`${InvoiceFormFields.line_items}.${index}.quantity`}>
                                Quantité
                              </Label>
                              <Controller
                                name={`${InvoiceFormFields.line_items}.${index}.quantity`}
                                control={control}
                                rules={{
                                  required: "Requis",
                                  min: { value: 1, message: "Min 1" },
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    type="number"
                                    min="1"
                                    placeholder="1.00"
                                    // disabled={isUpdate}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={errors}
                                name={`${InvoiceFormFields.line_items}.${index}.quantity`}
                                render={({ message }) => (
                                  <p className="text-sm text-red-500">
                                    {message}
                                  </p>
                                )}
                              />
                            </div>

                            {/* Prix unitaire */}
                            <div className="space-y-2 w-full">
                              <Label
                                htmlFor={`${InvoiceFormFields.line_items}.${index}.unit_price`}>
                                Prix unitaire{" "}
                              </Label>
                              <Controller
                                name={`${InvoiceFormFields.line_items}.${index}.unit_price`}
                                control={control}
                                rules={{
                                  required: "Requis",
                                  min: { value: 0.01, message: "Min 0.01" },
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    placeholder="5000.00"
                                    // disabled={isUpdate}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={errors}
                                name={`${InvoiceFormFields.line_items}.${index}.unit_price`}
                                render={({ message }) => (
                                  <p className="text-sm text-red-500">
                                    {message}
                                  </p>
                                )}
                              />
                            </div>

                            {/* Remise */}

                            <div className={cn(
                              "space-y-2 w-full",
                              watchReductionBase === INVOICE_REDUCTION_BASE.PER_LINE? "block" : "hidden"
                            )}>
                              <Label
                                htmlFor={`${InvoiceFormFields.line_items}.${index}.reduction_value`}>
                                Réduction{" "}
                                {watchReductionType ===
                                  INVOICE_REDUCTION_TYPE.PERCENTAGE && "%"}
                              </Label>
                              <Input
                                type="text"
                                id={`${InvoiceFormFields.line_items}.${index}.reduction_value`}
                                {...register(
                                  `${InvoiceFormFields.line_items}.${index}.reduction_value`
                                )}
                              />
                            </div>

                            {/* Montant */}
                            <div className="space-y-2 w-full">
                              <Label
                                htmlFor={`${InvoiceFormFields.line_items}.${index}.amount`}>
                                Montant
                              </Label>
                              <Input
                                type="text"
                                id={`${InvoiceFormFields.line_items}.${index}.amount`}
                                {...register(
                                  `${InvoiceFormFields.line_items}.${index}.amount`
                                )}
                                value={calculateItemAmount(
                                  watchItems[index]?.quantity || "0",
                                  watchItems[index]?.unit_price || "0",
                                  watchItems[index]?.reduction_value || "0"
                                )}
                                disabled
                                className="bg-gray-100"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Totaux */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-black4">Totaux</h3>

                <div className="bg-blue-50 rounded-lg p-6 space-y-1">
                  <div className="flex justify-between items-center">
                    <Label className="">Montant HT</Label>
                    <div className="w-48 text-right font-semibold px-3 py-2">
                      {calculateTotalHT().toFixed(2)}
                    </div>
                  </div>

                  {calculateReductionAmount() > 0 && (
                    <>
                      <div className="flex justify-between items-center ">
                        <Label className="">
                          Réduction totale
                          {/*{getReductionLabel(*/}
                          {/*  watchReductionBase,*/}
                          {/*  watchReductionType,*/}
                          {/*  watchReductionValue*/}
                          {/*)}*/}
                        </Label>
                        <span className="w-48 text-right font-semibold px-3 py-2">
                          -
                          {(
                            calculateTotalHT() - calculateAfterReduction()
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <Label className="">Sous-Total HT</Label>
                        <div className="w-48 text-right font-semibold px-3 py-2">
                          {calculateAfterReduction().toFixed(2)}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center">
                    <Label className="">Taxe ({watchTva}%)</Label>
                    <div className="w-48 text-right font-semibold px-3 py-2">
                      {calculateTax().toFixed(2)}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-300">
                    <Label className=" font-bold">Total TTC</Label>
                    <div className="w-48 text-right font-semibold px-3 py-2">
                      {calculateTotalTTC().toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <Button type="submit" className="bg-blue-700">
                  {isUpdate ? "Modifier" : "Émettre"} la facture
                </Button>
              </div>
            </form>
          </>
        </div>
      </div>
    </div>
  )
}

export default AddOrModifyInvoice;